import React, { useEffect, useState } from 'react';
import {
    ArrowSquareOut,
    EnvelopeSimple,
    HouseLine,
    MapPin,
    MapTrifold,
    Phone,
    User,
    UserCircle,
} from '@phosphor-icons/react';
import { Skeleton } from 'antd';
import opportunityInfoService from '../../../../services/oneButtonServices/opportunityInfoService';
import { useOneButtonStore, COMMON_CONSTANTS } from '../../../../store/oneButtonStore';
import designServices from '../../../../services/oneButtonServices/designServices';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import {
    buildCapabilityFinancePartnerDisplayNames,
    filterFinancePartnerMasterListByAllowList,
} from '../../../../services/oneButtonServices/capabilityService';
import type { OpportunitySalesPerson } from '../../../../@types/oneButtonType';
import SalesPersonModal from './SalesPersonModal';

export interface OpportunityDetailsCardProps {
    loading?: boolean;
    opportunityId?: string | null;
    opportunityName?: string | null;
    stageStatus?: string | null;
    stageNumber?: string | null;
    utilityCompany?: string | null;
    siteAddress?: string | null;
    onRefresh?: () => void;
    refreshKey?: number;
}

export const OpportunityDetailsCard: React.FC<OpportunityDetailsCardProps> = ({
    loading: propsLoading = false,
    opportunityId,
    opportunityName,
    siteAddress,
    stageStatus,
    stageNumber,
    onRefresh,
    refreshKey = 0,
}) => {
    const [apiDetails, setApiDetails] = useState<any>(null);
    const [loadingApi, setLoadingApi] = useState(false);
    const [salesPersonModalOpen, setSalesPersonModalOpen] = useState(false);
    /** When the opportunity detail API fails (e.g. 400), show a message in the card */
    const [loadError, setLoadError] = useState<string | null>(null);
    const setOpportunityID = useOneButtonStore((s) => s.setOpportunityID);
    const setOpportunityDetails = useOneButtonStore((s) => s.setOpportunityDetails);
    const setSolarUtilityData = useOneButtonStore((s) => s.setSolarUtilityData);
    const setOpportunityinfoMasterData = useOneButtonStore((s) => s.setOpportunityinfoMasterData);
    const setEquipmentMasterData = useOneButtonStore((s) => s.setEquipmentMasterData);
    const setProductTypeList = useOneButtonStore((s) => s.setProductTypeList);
    const setFinancePartnersList = useOneButtonStore((s) => s.setFinancePartnersList);
    const opportunityID = useOneButtonStore((s) => s.opportunityID);
    const setTitleInfo = useOneButtonStore((s) => s.setTitleInfo);
    /** Callback handled locally in the dashboard */
    const activeSfid =  useOneButtonStore((s) => s.salesForceId);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const opportunityInitManaged = useOneButtonStore((s) => s.opportunityInitManagedByOrchestrator);
    const isOpportunityInitializing = useOneButtonStore((s) => s.isOpportunityInitializing);
    const opportunityLoadErrorFromStore = useOneButtonStore((s) => s.opportunityLoadError);

    useEffect(() => {
        if (!activeSfid || activeSfid === '—') return;

        if (opportunityInitManaged) {
            setApiDetails(opportunityDetails);
            setLoadError(opportunityLoadErrorFromStore);
            setLoadingApi(isOpportunityInitializing);
            return;
        }

        setLoadError(null);
        setLoadingApi(true);
        opportunityInfoService
            .getOpportunityDetail(activeSfid)
            .then((data) => {
                const actualData = data != null ? (data?.data ?? data) : null;
                setApiDetails(actualData);
                setOpportunityID(actualData?.Opportunity_Id ?? null);
                setOpportunityDetails(actualData);
                if (actualData == null) {
                    setLoadError('Opportunity details could not be loaded (e.g. invalid or not found).');
                }
            })
            .catch(() => {
                setApiDetails(null);
                setOpportunityDetails(null);
                setLoadError('Opportunity details could not be loaded.');
            })
            .finally(() => setLoadingApi(false));
    }, [
        activeSfid,
        setOpportunityDetails,
        refreshKey,
        opportunityInitManaged,
        opportunityDetails,
        isOpportunityInitializing,
        opportunityLoadErrorFromStore,
        setOpportunityID,
    ]);

    useEffect(() => {
        if (activeSfid) onRefresh?.();
    }, [activeSfid]);

    const setOpportunityLoadError = useOneButtonStore((s) => s.setOpportunityLoadError);
    useEffect(() => {
        setOpportunityLoadError(loadError);
        return () => setOpportunityLoadError(null);
    }, [loadError, setOpportunityLoadError]);

    useEffect(() => {
        if (opportunityInitManaged) return;
        if (!opportunityID) {
            setSolarUtilityData(null);
            return;
        }
        designServices.RetrieveUtilityInformation(opportunityID).then((result) => {
            setSolarUtilityData(result?.utilityDetails ?? null);
        }).catch(() => {
            setSolarUtilityData(null);
        });
    }, [opportunityID, setSolarUtilityData, opportunityInitManaged]);

    useEffect(() => {
        if (opportunityInitManaged) return;

        let cancelled = false;

        const loadMasterData = async () => {
            try {
                await useOneButtonStore.getState().fetchOnebuttonCapabilities(false);
                const caps = useOneButtonStore.getState().onebuttonCapabilities;

                const res = await masterServices.GetMasterDataByStep({
                    step: 1,
                    paramDTOs: [{ key: 'string', value: 'string' }],
                });
                if (cancelled) return;

                const data = res?.data || res;
                setOpportunityinfoMasterData(data);

                // Filter Products (parity with Angular opportunity-info getMasterData product loop, minus RR_Visibility)
                if (data?.ProductType && Array.isArray(data.ProductType)) {
                    const productsList: any[] = [];
                    data.ProductType.forEach((ele: any) => {
                        if (
                            ele.Text === COMMON_CONSTANTS.SolarProductName ||
                            ele.Text === COMMON_CONSTANTS.BatteryProductName ||
                            ele.Text === COMMON_CONSTANTS.RoofProductName ||
                            ele.Text === COMMON_CONSTANTS.RRRProductName ||
                            ele.Text === COMMON_CONSTANTS.RRProduct
                        ) {
                            productsList.push(ele);
                        }
                    });
                    setProductTypeList(productsList);
                }

                // Filter Finance Partners — Angular: financePartnersList only includes master rows whose
                // `Text` appears in authService.capabilityFinancePartners (coarse Iscash / IsServiceFinance / …).
                const rawPartners = data?.FinancePartnerName;
                if (Array.isArray(rawPartners)) {
                    let list: typeof rawPartners = rawPartners;
                    if (caps?.capabilities) {
                        const allowed = buildCapabilityFinancePartnerDisplayNames(caps);
                        list =
                            allowed.length > 0
                                ? filterFinancePartnerMasterListByAllowList(rawPartners, allowed)
                                : [];
                    }
                    setFinancePartnersList(list);
                } else {
                    setFinancePartnersList([]);
                }
            } catch (err) {
                console.error('Error fetching global master data:', err);
            }
        };

        void loadMasterData();
        return () => {
            cancelled = true;
        };
    }, [setOpportunityinfoMasterData, setProductTypeList, setFinancePartnersList, opportunityInitManaged]);

    useEffect(() => {
        if (opportunityInitManaged) return;

        const solarProduct = apiDetails?.OpportunityProduct?.find((p: any) => (p.Product_Type_Name === 'Solar'));
        const financePartnerId = solarProduct?.Finance_Partner_Id;

        if (financePartnerId) {
            designServices.GetEquipmentMasterList(financePartnerId)
                .then((res: any) => {
                    setEquipmentMasterData(res?.data || res);
                })
                .catch((err) => console.error("Error fetching equipment master data:", err));
        }
    }, [apiDetails, setEquipmentMasterData, opportunityInitManaged]);

    useEffect(() => {
        if (opportunityInitManaged) return;

        const directLeadId = apiDetails?.DirectLeadID;
        const installationAddress = apiDetails?.InstallationAddress;

        if (directLeadId && installationAddress) {
            opportunityInfoService.getCustomerTitleInfo(directLeadId, installationAddress)
                .then((res) => {
                    // Extract from data.locations[0] based on confirmed response format
                    setTitleInfo(res?.data?.locations?.[0]);
                })
                .catch((err) => console.error("Error fetching customer title info:", err));
        }
    }, [apiDetails, setTitleInfo, opportunityInitManaged]);

    const loading = propsLoading || (opportunityInitManaged ? isOpportunityInitializing : loadingApi);
    // Prefer store-backed details so edits (e.g. ContactManageModal save) reflect immediately.
    // Fall back to apiDetails for initial load / transient store nulls.
    const details = opportunityDetails ?? apiDetails;
    const primaryContact = details?.Customers?.find((c: any) => c.IsPrimaryContact) || details?.Customers?.[0];
    const installAddress = details?.InstallationAddress;
    const contactZipCode = installAddress?.ZipCode ?? installAddress?.zipCode ?? '';
    const addressLine = installAddress
        ? [installAddress.Street, installAddress.City ? `${installAddress.City}, ${installAddress.stateCode || installAddress.State}`.trim() : ''].filter(Boolean).join(', ')
        : siteAddress || '';

    const oppId = details?.SalesForce_Id || opportunityId || '';
    const oneLeadLink = details?.OneLeadId ?? details?.OneLeadLink ?? details?.oneLeadId ?? details?.oneLeadLink ?? '';

    const primaryName = primaryContact
        ? [primaryContact.LastName, primaryContact.FirstName].filter(Boolean).join(', ') || `${primaryContact.FirstName || ''} ${primaryContact.LastName || ''}`.trim()
        : '';
    const primaryPhone = primaryContact?.Phone || '';
    const primaryEmail = primaryContact?.Email || '';

    const salesPerson = details?.SalesPerson as OpportunitySalesPerson | undefined;
    const hasSalesPerson =
        !!salesPerson &&
        [salesPerson.Name, salesPerson.Phone, salesPerson.Email, salesPerson.Sales_Division, salesPerson.Team].some(
            (v) => v != null && String(v).trim() !== ''
        );

    if (loading) {
        return (
            <div className="obd-opportunity-cards-wrap obd-opportunity-cards-wrap--v3">
                <div className="obd-card obd-card--details obd-card--details-ref obd-dashboard-main-card">
                    <Skeleton active title={{ width: '70%' }} paragraph={{ rows: 6, width: ['90%', '60%', '80%', '70%', '50%', '40%'] }} />
                </div>
            </div>
        );
    }

    const fieldIconProps = { size: 14, weight: 'duotone' as const };

    const TileCol: React.FC<{ label: string; value: React.ReactNode; icon?: React.ReactNode }> = ({ label, value, icon }) => (
        <div className="obd-detail-tile__col">
            <span
                className={
                    icon
                        ? 'obd-detail-tile__col-label obd-detail-tile__col-label--with-field-icon'
                        : 'obd-detail-tile__col-label'
                }
            >
                {icon ? <span className="obd-detail-tile__field-icon" aria-hidden>{icon}</span> : null}
                {label}
            </span>
            <span className="obd-detail-tile__col-value">{value ?? '—'}</span>
        </div>
    );

    return (
        <div className="obd-opportunity-cards-wrap obd-opportunity-cards-wrap--v3">
            {/* Card 1: Opportunity & Contact — tile style (primary + two-column bottom) */}
            <div className="obd-card obd-card--details obd-card--opportunity-contact obd-dashboard-main-card">
                <div className="obd-detail-tile">
                    <div className="obd-dashboard-main-card__head">
                        <div className="obd-detail-tile__top">
                            <div className="obd-detail-tile__top-row">
                                <div className="obd-detail-tile__top-left obd-detail-tile__top-left--with-icon">
                                    <span className="obd-section-head-icon obd-section-head-icon--sm" aria-hidden>
                                        <UserCircle size={18} weight="duotone" />
                                    </span>
                                    <div className="obd-detail-tile__title-stack">
                                        <span className="obd-detail-tile__label">OPPORTUNITY &amp; CONTACT</span>
                                    </div>
                                </div>
                                <div className="obd-detail-tile__top-right obd-detail-tile__top-right--identity">
                                    <div className="obd-detail-tile__identity-row">
                                        {oppId ? (
                                            <a
                                                href="#"
                                                className="obd-opportunity-overview__link obd-detail-tile__link obd-detail-tile__identity-link obd-detail-tile__identity-link--with-icon"
                                                onClick={(e) => { e.preventDefault(); window.open(`${import.meta.env.VITE_SALESFORCE_URL}${oppId}/view`, '_blank'); }}
                                            >
                                                <span className="obd-detail-tile__identity-link-text">{oppId}</span>
                                                <ArrowSquareOut size={14} weight="duotone" aria-hidden className="obd-detail-tile__identity-external-icon" />
                                            </a>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="obd-dashboard-main-card__body">
                        <div className="obd-detail-tile__body-primary">
                            <span className="obd-detail-tile__primary obd-detail-tile__primary--with-icon">
                                <span className="obd-detail-tile__identity-name-icon" aria-hidden>
                                    <User size={16} weight="duotone" />
                                </span>
                                <span className="obd-detail-tile__primary-text">
                                    {primaryName || oppId || '—'}
                                </span>
                            </span>
                        </div>
                        <div className="obd-detail-tile__divider" aria-hidden />
                        <div className="obd-detail-tile__bottom">
                            <div className="obd-detail-tile__row">
                                <TileCol label="Phone" value={primaryPhone} icon={<Phone {...fieldIconProps} />} />
                                <TileCol label="Email" value={primaryEmail} icon={<EnvelopeSimple {...fieldIconProps} />} />
                            </div>
                            <div className="obd-detail-tile__row">
                                <div className="obd-detail-tile__col">
                                    <span className="obd-detail-tile__col-label obd-detail-tile__col-label--with-field-icon">
                                        <span className="obd-detail-tile__field-icon" aria-hidden>
                                            <HouseLine {...fieldIconProps} />
                                        </span>
                                        Address
                                    </span>
                                    <span className="obd-detail-tile__col-value obd-detail-tile__col-value--with-action">
                                        {addressLine || '—'}
                                        {addressLine && (
                                            <a
                                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([addressLine, contactZipCode].filter(Boolean).join(', '))}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="obd-detail-tile__map-link"
                                                aria-label="Open address in Google Maps"
                                                title="Open in Google Maps"
                                            >
                                                <MapTrifold size={18} weight="duotone" />
                                            </a>
                                        )}
                                    </span>
                                </div>
                                <TileCol label="Zip Code" value={contactZipCode} icon={<MapPin {...fieldIconProps} />} />
                            </div>
                            {oneLeadLink ? (
                                <div className="obd-detail-tile__row">
                                    <div className="obd-detail-tile__col obd-detail-tile__col--full">
                                        <span className="obd-detail-tile__col-label">oneLEAD Link</span>
                                        <a href="#" className="obd-opportunity-overview__link obd-detail-tile__col-value" onClick={(e) => e.preventDefault()}>{oneLeadLink}</a>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                    {hasSalesPerson ? (
                        <button
                            type="button"
                            className="obd-merged-system__chip-btn obd-detail-tile__salesperson-trigger obd-detail-tile__salesperson-trigger--corner"
                            onClick={() => setSalesPersonModalOpen(true)}
                            aria-label="View sales person"
                        >
                            Sales Person
                        </button>
                    ) : null}
                </div>
            </div>
            <SalesPersonModal
                open={salesPersonModalOpen}
                onClose={() => setSalesPersonModalOpen(false)}
                salesPerson={salesPerson}
            />
        </div>
    );
};

export default OpportunityDetailsCard;
